import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { cn } from '@/lib/cn'
import { type VariantProps, cva } from 'class-variance-authority'
import { type ButtonHTMLAttributes, forwardRef } from 'react'

const buttonVariants = cva(
  'group/btn relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] min-h-[44px]',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground shadow-sm shadow-primary/20 hover:bg-primary-hover hover:shadow-md hover:shadow-primary/25',
        secondary:
          'bg-muted text-foreground hover:bg-muted/70',
        outline:
          'border border-border bg-background text-foreground shadow-sm hover:bg-muted hover:shadow-md',
        ghost: 'text-foreground hover:bg-muted',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm shadow-destructive/20 hover:bg-destructive/90 hover:shadow-md hover:shadow-destructive/25',
      },
      size: {
        sm: 'h-9 min-h-[36px] px-3 text-xs',
        md: 'h-11 px-4',
        lg: 'h-12 px-6 text-base',
        icon: 'h-11 w-11 min-w-[44px] p-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
  loadingLabel?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, loading = false, loadingLabel, disabled, children, ...props },
    ref,
  ) => {
    const isIcon = size === 'icon'
    const spinnerSize = size === 'sm' ? 14 : size === 'lg' ? 18 : 16

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {loading ? (
          <>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
            >
              <LoadingSpinner size={spinnerSize} variant="ring" />
            </span>
            <span className={cn('flex items-center gap-2', isIcon ? 'invisible' : 'opacity-0')}>
              {children}
            </span>
            {loadingLabel ? <span className="sr-only">{loadingLabel}</span> : null}
          </>
        ) : (
          children
        )}
      </button>
    )
  },
)
Button.displayName = 'Button'
